import React, { useState, useEffect } from 'react';
import fishService from '../services/fishService.js';
import { Link, useNavigate } from 'react-router-dom';
import { fishDataFromCsv as fishData } from '../data/fishDataFromCsv.js';
import CsvImport from '../components/CsvImport.js';
import HtmlImport from '../components/HtmlImport.js';
import ImageUpload from '../components/ImageUpload.js';
import ImageSelector from '../components/ImageSelector.js';
import ImageManager from '../components/ImageManager.js';
import { StorageManager } from '../utils/storageManager.js';

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [fishList, setFishList] = useState(() => {
    // Load fish from localStorage using StorageManager
    const savedFish = StorageManager.loadFishData();
    if (savedFish) {
      return savedFish;
    }
    return [...fishData];
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFish, setEditingFish] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    scientific_name: '',
    price: '',
    description: '',
    care_level: 'beginner',
    size: '',
    temperament: '',
    reef_safe: true,
    diet: '',
    origin: '',
    image_url: '',
    is_available: true,
    stock_quantity: 1,
    category: '',
    tank_size: ''
  });

  // Simple auth check (in production, use proper authentication)
  useEffect(() => {
    const adminAuth = localStorage.getItem('reeffish_admin_auth');
    if (adminAuth === 'authenticated') {
      setIsAuthenticated(true);
      setShowLoginForm(false);
    }
  }, []);

  // Save fish list to localStorage whenever it changes
  useEffect(() => {
    const result = StorageManager.saveFishData(fishList);
    if (!result.success) {
      console.error('Failed to save fish data:', result.error);
      // Show user-friendly error message
      if (result.suggestion) {
        alert(`Storage Error: ${result.error}\n\n${result.suggestion}`);
      }
    } else if (result.compressed) {
      console.log('Fish data was compressed to fit in storage');
    }
  }, [fishList]);

  const handleLogin = (e) => {
    e.preventDefault();
    // Simple demo auth (replace with real auth in production)
    if (credentials.username === 'admin' && credentials.password === 'Kaaminee1985') {
      setIsAuthenticated(true);
      setShowLoginForm(false);
      localStorage.setItem('reeffish_admin_auth', 'authenticated');
    } else {
      alert('Invalid credentials. Try admin / reef2024');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowLoginForm(true);
    localStorage.removeItem('reeffish_admin_auth');
    navigate('/');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      scientific_name: '',
      price: '',
      description: '',
      care_level: 'beginner',
      size: '',
      temperament: '',
      reef_safe: true,
      diet: '',
      origin: '',
      image_url: '',
      is_available: true,
      stock_quantity: 1,
      category: '',
      tank_size: ''
    });
    setEditingFish(null);
  };

  const handleAddFish = () => {
    resetForm();
    setShowAddForm(true);
  };

  const handleEditFish = (fish) => {
    // Clean the fish data before setting it to form
    const cleanFishData = {
      ...fish,
      image_url: fish.image_url === null || fish.image_url === 'null' || !fish.image_url ? '' : fish.image_url
    };
    console.log('Editing fish:', cleanFishData); // Debug log
    setFormData(cleanFishData);
    setEditingFish(fish);
    setShowAddForm(true);
  };

  const handleSubmitFish = (e) => {
    e.preventDefault();
    
    const fishData = {
      ...formData,
      id: editingFish ? editingFish.id : Date.now(),
      price: parseFloat(formData.price),
      stock_quantity: parseInt(formData.stock_quantity),
      // Ensure image_url is properly set (null if empty, not empty string)
      image_url: formData.image_url && formData.image_url.trim() !== '' ? formData.image_url : null
    };

    if (editingFish) {
      // Update existing fish
      const updatedList = fishList.map(f => f.id === editingFish.id ? fishData : f);
      setFishList(updatedList);
      // Force update to localStorage immediately with error handling
      const result = StorageManager.saveFishData(updatedList);
      if (!result.success) {
        alert(`Failed to save changes: ${result.error}\n${result.suggestion || ''}`);
        return; // Don't proceed if save failed
      }
      console.log('Updated fish:', fishData); // Debug log
    } else {
      // Add new fish
      const newList = [...fishList, fishData];
      setFishList(newList);
      // Force update to localStorage immediately with error handling
      const result = StorageManager.saveFishData(newList);
      if (!result.success) {
        alert(`Failed to save new fish: ${result.error}\n${result.suggestion || ''}`);
        return; // Don't proceed if save failed
      }
    }

    setShowAddForm(false);
    resetForm();
    
    // Force a page refresh to ensure changes are visible
    window.location.reload();
  };

  const handleDeleteFish = (id) => {
    if (window.confirm('Are you sure you want to delete this fish?')) {
      setFishList(prev => prev.filter(f => f.id !== id));
    }
  };

  const handleBulkImport = (importedFish) => {
    setFishList(prev => [...prev, ...importedFish]);
  };

  const toggleAvailability = (id) => {
    setFishList(prev => prev.map(f => 
      f.id === id ? { ...f, is_available: !f.is_available } : f
    ));
  };

  const getStockStatus = (fish) => {
    if (!fish.is_available || fish.stock_quantity === 0) {
      return { text: 'Out of Stock', class: 'stock-out' };
    } else if (fish.stock_quantity <= 2) {
      return { text: 'Low Stock', class: 'stock-low' };
    } else {
      return { text: 'In Stock', class: 'stock-in' };
    }
  };

  const stats = {
    total: fishList.length,
    available: fishList.filter(f => f.is_available && f.stock_quantity > 0).length,
    outOfStock: fishList.filter(f => !f.is_available || f.stock_quantity === 0).length,
    lowStock: fishList.filter(f => f.is_available && f.stock_quantity <= 2 && f.stock_quantity > 0).length
  };

  if (showLoginForm) {
    return (
      <div style={{ 
        background: 'var(--gradient-tropical)', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div className="card" style={{ maxWidth: '400px', width: '100%', margin: '20px' }}>
          <h2 style={{ 
            fontFamily: 'Playfair Display, serif', 
            textAlign: 'center', 
            marginBottom: '30px',
            color: 'var(--tropical-deep)'
          }}>
            🐠 Admin Access
          </h2>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Username
              </label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--tropical-aqua)',
                  borderRadius: 'var(--radius)',
                  fontSize: '16px'
                }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Password
              </label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--tropical-aqua)',
                  borderRadius: 'var(--radius)',
                  fontSize: '16px'
                }}
              />
            </div>
            <button type="submit" className="btn" style={{ width: '100%', marginBottom: '15px' }}>
              Login to Admin
            </button>
            <div style={{ textAlign: 'center' }}>
              <Link to="/" style={{ color: 'var(--tropical-aqua)', textDecoration: 'none' }}>
                ← Back to Store
              </Link>
            </div>
          </form>
          <div style={{ 
            marginTop: '20px', 
            padding: '10px', 
            background: 'rgba(255,255,255,0.5)', 
            borderRadius: '8px',
            fontSize: '12px',
            textAlign: 'center'
          }}>
            Demo: admin / reef2024
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div style={{ background: 'var(--gradient-tropical)', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '15px 0',
        boxShadow: 'var(--shadow-card)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <Link to="/" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                color: 'var(--tropical-aqua)', 
                textDecoration: 'none',
                fontSize: '14px'
              }}>
                ← Back to Store
              </Link>
              <div style={{ width: '1px', height: '20px', background: '#ddd' }}></div>
              <h1 style={{ 
                fontFamily: 'Playfair Display, serif', 
                fontSize: '24px',
                color: 'var(--midnight-blue)',
                margin: 0
              }}>
                🐠 SaltFishy Admin
              </h1>
            </div>
            <button 
              onClick={handleLogout}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container" style={{ paddingTop: '30px', paddingBottom: '30px' }}>
        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          marginBottom: '40px'
        }}>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>🐟</div>
            <h3 style={{ fontSize: '32px', color: 'var(--tropical-aqua)', margin: 0 }}>{stats.total}</h3>
            <p style={{ color: '#666', margin: 0 }}>Total Fish</p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>✅</div>
            <h3 style={{ fontSize: '32px', color: 'var(--tropical-lime)', margin: 0 }}>{stats.available}</h3>
            <p style={{ color: '#666', margin: 0 }}>Available</p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>⚠️</div>
            <h3 style={{ fontSize: '32px', color: 'var(--tropical-sun)', margin: 0 }}>{stats.lowStock}</h3>
            <p style={{ color: '#666', margin: 0 }}>Low Stock</p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>❌</div>
            <h3 style={{ fontSize: '32px', color: 'var(--tropical-coral)', margin: 0 }}>{stats.outOfStock}</h3>
            <p style={{ color: '#666', margin: 0 }}>Out of Stock</p>
          </div>
        </div>

        {/* Bulk Import Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '20px',
          marginBottom: '40px'
        }}>
          <CsvImport onImportComplete={handleBulkImport} />
          <HtmlImport onImportComplete={handleBulkImport} />
        </div>


        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', color: 'white', margin: 0 }}>
            Fish Inventory ({fishList.length} total)
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={handleAddFish}
              className="btn"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              + Add Fish
            </button>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => {
                  if (window.confirm('This will clear all imported fish and reset to original data. Are you sure?')) {
                    setFishList([...fishData]);
                    localStorage.removeItem(StorageManager.STORAGE_KEY);
                    // Also clear any other old storage data
                    StorageManager.clearOldData();
                    window.location.reload();
                  }
                }}
                className="admin-button"
                style={{
                  background: 'var(--coral) !important',
                  borderColor: 'var(--coral) !important',
                  fontSize: '14px'
                }}
              >
                🔄 Reset to Default
              </button>
              
              <button 
                onClick={() => {
                  const storageInfo = StorageManager.getStorageInfo(fishList);
                  const analysis = StorageManager.analyzeStorage();
                  
                  let message = `Storage Usage:\n`;
                  message += `Current fish data: ${storageInfo.sizeInKB} KB (${storageInfo.percentUsed}%)\n`;
                  message += `Total localStorage: ${analysis.totalSizeKB} KB\n`;
                  message += `Fish count: ${fishList.length}\n\n`;
                  
                  if (storageInfo.isNearLimit) {
                    message += `⚠️ WARNING: Approaching storage limit!\n`;
                    message += `Consider reducing fish count or image sizes.\n\n`;
                  }
                  
                  if (analysis.items.length > 1) {
                    message += `Top storage items:\n`;
                    analysis.items.slice(0, 3).forEach(item => {
                      message += `• ${item.key}: ${item.sizeKB} KB\n`;
                    });
                  }
                  
                  alert(message);
                }}
                className="admin-button"
                style={{
                  background: 'var(--ocean-mid) !important',
                  borderColor: 'var(--ocean-mid) !important',
                  fontSize: '14px'
                }}
              >
                📊 Check Storage
              </button>
              
              <button 
                onClick={() => {
                  if (window.confirm('This will clean up old storage data that may be taking up space. Continue?')) {
                    const removedCount = StorageManager.clearOldData();
                    const storageInfo = StorageManager.getStorageInfo(fishList);
                    alert(`Cleanup complete!\n\nRemoved ${removedCount} old storage items\nCurrent fish data: ${storageInfo.sizeInKB} KB (${storageInfo.percentUsed}%)`);
                  }
                }}
                className="admin-button"
                style={{
                  background: 'var(--tropical-lime) !important',
                  borderColor: 'var(--tropical-lime) !important',
                  fontSize: '14px'
                }}
              >
                🧹 Clean Storage
              </button>
            </div>
          </div>
        </div>

        {/* Fish List */}
        <div style={{ display: 'grid', gap: '15px' }}>
          {fishList.map(fish => {
            const stockStatus = getStockStatus(fish);
            return (
              <div key={fish.id} className="card" style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '20px', alignItems: 'center' }}>
                  {/* Fish Image */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: fish.image_url ? `url(${fish.image_url})` : 'var(--gradient-ocean)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: 'var(--radius)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '30px'
                  }}>
                    {!fish.image_url && fish.image}
                  </div>

                  {/* Fish Info */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <h3 style={{ margin: 0, fontFamily: 'Playfair Display, serif' }}>{fish.name}</h3>
                      {fish.category && (
                        <span style={{
                          padding: '2px 8px',
                          background: 'var(--tropical-aqua)',
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}>
                          {fish.category}
                        </span>
                      )}
                      <span className={`stock-status ${stockStatus.class}`} style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: 'bold'
                      }}>
                        {stockStatus.text}
                      </span>
                    </div>
                    {fish.scientific_name && (
                      <p style={{ margin: '0 0 8px 0', fontStyle: 'italic', color: '#666', fontSize: '14px' }}>
                        {fish.scientific_name}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
                      <span><strong>Price:</strong> ${fish.price}</span>
                      <span><strong>Stock:</strong> {fish.stock_quantity}</span>
                      <span><strong>Care:</strong> {fish.care_level}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* Delete Image */}
                    {fish.image_url && (
                      <button
                        title="Delete image from storage"
                        onClick={async () => {
                          if (!window.confirm('Delete this image from storage?')) return;
                          try {
                            await fishService.deleteFishImage(fish.image_url);
                            // Clear image_url locally
                            const updated = fishList.map(f => f.id === fish.id ? { ...f, image_url: '' } : f);
                            setFishList(updated);
                            StorageManager.saveFishData(updated);
                            alert('Image deleted successfully');
                          } catch (e) {
                            alert(`Failed to delete image: ${e.message}`);
                          }
                        }}
                        style={{
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: 'var(--radius)',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          background: 'var(--tropical-coral)',
                          color: 'white',
                          transition: 'all 0.3s ease',
                          boxShadow: 'var(--shadow-soft)'
                        }}
                      >
                        🧹 Delete Image
                      </button>
                    )}

                    <button
                      onClick={() => toggleAvailability(fish.id)}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: 'var(--radius)',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        background: fish.is_available ? 'var(--tiffany-blue)' : 'var(--blue-grotto)',
                        color: 'white',
                        transition: 'all 0.3s ease',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: 'var(--shadow-soft)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = 'var(--shadow-card)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'var(--shadow-soft)';
                      }}
                    >
                      {fish.is_available ? '✅ Available' : '❌ Disabled'}
                    </button>
                    <button
                      onClick={() => handleEditFish(fish)}
                      className="btn btn-secondary"
                      style={{ 
                        padding: '8px 16px', 
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteFish(fish.id)}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: 'var(--radius)',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        background: 'var(--accent-coral)',
                        color: 'white',
                        transition: 'all 0.3s ease',
                        boxShadow: 'var(--shadow-soft)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = 'var(--shadow-card)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'var(--shadow-soft)';
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add/Edit Fish Modal */}
        {showAddForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div className="card" style={{ 
              maxWidth: '600px', 
              width: '100%', 
              maxHeight: '80vh', 
              overflow: 'auto',
              margin: 0
            }}>
              <h3 style={{ 
                fontFamily: 'Playfair Display, serif',
                marginBottom: '20px',
                color: 'var(--tropical-deep)'
              }}>
                {editingFish ? 'Edit Fish' : 'Add New Fish'}
              </h3>
              
              <form onSubmit={handleSubmitFish}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Scientific Name
                    </label>
                    <input
                      type="text"
                      value={formData.scientific_name}
                      onChange={(e) => setFormData({...formData, scientific_name: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '6px'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Price ($) *
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      required
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Stock Quantity
                    </label>
                    <input
                      type="number"
                      value={formData.stock_quantity}
                      onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Care Level
                    </label>
                    <select
                      value={formData.care_level}
                      onChange={(e) => setFormData({...formData, care_level: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid #ddd',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>
                </div>

                <ImageManager
                  value={formData.image_url}
                  onChange={(url) => {
                    console.log('Image URL changed to:', url); // Debug
                    setFormData({...formData, image_url: url});
                  }}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Category
                    </label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      placeholder="e.g., Clownfish, Tang"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid var(--baby-blue)',
                        borderRadius: 'var(--radius)'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Tank Size
                    </label>
                    <input
                      type="text"
                      value={formData.tank_size}
                      onChange={(e) => setFormData({...formData, tank_size: e.target.value})}
                      placeholder="e.g., 50+ gal"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '2px solid var(--baby-blue)',
                        borderRadius: 'var(--radius)'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '25px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      resetForm();
                    }}
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    style={{ flex: 1 }}
                  >
                    {editingFish ? 'Update' : 'Add'} Fish
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
